import { Card, Collapse, Icon, MenuItem, Select, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import DataTableCustom from "components/Tables/DataTableCustom";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import { ESTADOS } from "layouts/pagos/config";

const CONTROL_SX = {
  minHeight: 42,
  height: "auto",
  padding: "0 !important",
  backgroundColor: "#fff",
  borderRadius: "10px",
  color: "#344563",
  fontSize: "0.875rem",
  fontWeight: 500,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d7deea" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9eb0c8" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#347ff0", borderWidth: 1 },
  "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(52, 127, 240, 0.12)" },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center",
    boxSizing: "border-box",
    minWidth: 0,
    minHeight: "42px !important",
    height: "auto !important",
    lineHeight: 1.35,
    overflow: "visible !important",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    paddingTop: "11px !important",
    paddingRight: "38px !important",
    paddingBottom: "11px !important",
    paddingLeft: "44px !important",
  },
  "& .MuiSelect-icon": { right: 12, color: "#344563" },
};

const DATE_OPTIONS = [
  { value: "", label: "Todas las fechas" },
  { value: "hoy", label: "Hoy" },
  { value: "ultimos7", label: "Últimos 7 días" },
  { value: "ultimos30", label: "Últimos 30 días" },
  { value: "anioActual", label: "Este año" },
];

const getDateFilterLabel = (value) =>
  DATE_OPTIONS.find((option) => option.value === value)?.label || DATE_OPTIONS[0].label;

const getFacturaEstado = (remito) => {
  const estado = remito?.pagosProveedor?.estado || "SinFacturar";
  return {
    key: estado,
    label: ESTADOS[estado]?.label || "Sin facturar",
    color: ESTADOS[estado]?.color || "uproPink",
  };
};

function FacturacionTab({
  loading,
  provisiones,
  search,
  estado,
  fecha,
  onSearchChange,
  onEstadoChange,
  onFechaChange,
  onClearFilters,
  expandedProvisions,
  onToggleProvision,
  remitosColumns,
  remitosActions,
  loadingFactura,
  pagination,
  onPageChange,
}) {
  return (
    <SoftBox id="facturacion-proveedor" mb={2}>
      <SoftBox mb={2.25}>
        <SoftBox>
          <SoftTypography variant="h4" color="dark" fontWeight="bold">
            Facturación
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Gestiona remitos, estados de factura y pagos
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      <Card
        sx={{
          border: "1px solid #e7ebf1",
          borderRadius: "12px",
          boxShadow: "0 5px 18px rgba(37, 52, 81, 0.07)",
          p: { xs: 1.5, sm: 2 },
          mb: 2,
        }}
      >
        <SoftBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "minmax(240px, 1fr) repeat(2, minmax(180px, 0.7fr))",
            xxl: "minmax(280px, 1fr) 300px 300px auto",
          }}
          gap={1.25}
          alignItems="center"
        >
          <SoftInput
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar por OP, remito o pedido..."
            icon={{ component: <Icon>search</Icon>, direction: "left" }}
            inputProps={{ "aria-label": "Buscar por OP, remito o nombre del pedido" }}
            sx={{ minHeight: 42 }}
          />

          <SoftBox position="relative" minWidth={0}>
            <Icon
              aria-hidden="true"
              sx={{
                position: "absolute",
                zIndex: 2,
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#5d6f89",
                fontSize: "1.15rem !important",
              }}
            >
              filter_alt
            </Icon>
            <Select
              fullWidth
              displayEmpty
              value={estado}
              title={ESTADOS[estado]?.label || "Todos los estados"}
              onChange={(event) => onEstadoChange(event.target.value)}
              inputProps={{ "aria-label": "Filtrar por estado de factura" }}
              sx={CONTROL_SX}
            >
              <MenuItem value="">Todos los estados</MenuItem>
              {Object.entries(ESTADOS).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </SoftBox>

          <SoftBox position="relative" minWidth={0}>
            <Icon
              aria-hidden="true"
              sx={{
                position: "absolute",
                zIndex: 2,
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#5d6f89",
                fontSize: "1.15rem !important",
              }}
            >
              calendar_today
            </Icon>
            <Select
              fullWidth
              displayEmpty
              value={fecha}
              title={`Fecha de entrega: ${getDateFilterLabel(fecha)}`}
              renderValue={(value) => `Entrega: ${getDateFilterLabel(value)}`}
              onChange={(event) => onFechaChange(event.target.value)}
              inputProps={{ "aria-label": "Filtrar por fecha de entrega" }}
              sx={CONTROL_SX}
            >
              {DATE_OPTIONS.map((option) => (
                <MenuItem key={option.value || "todas"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </SoftBox>

          <SoftButton
            variant="outlined"
            color="info"
            onClick={onClearFilters}
            sx={{
              minHeight: 42,
              whiteSpace: "nowrap",
              gridColumn: { xs: "1", sm: "1 / -1", xxl: "auto" },
              justifySelf: { xs: "stretch", xxl: "end" },
              "&:focus-visible": {
                outline: "3px solid rgba(52, 127, 240, 0.25)",
                outlineOffset: 2,
              },
            }}
          >
            <Icon sx={{ mr: 0.75 }}>filter_alt_off</Icon>
            Limpiar filtros
          </SoftButton>
        </SoftBox>
      </Card>

      {loading ? (
        <Card sx={{ py: 5 }}>
          <Loading />
        </Card>
      ) : provisiones.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center", border: "1px solid #e7ebf1" }}>
          <SoftBox color="text" mb={1}>
            <Icon sx={{ fontSize: "2rem !important" }}>receipt_long</Icon>
          </SoftBox>
          <SoftTypography variant="body2" color="text">
            No se encontraron órdenes de provisión con los filtros seleccionados
          </SoftTypography>
        </Card>
      ) : (
        <SoftBox display="flex" flexDirection="column" gap={1}>
          {provisiones.map((provision) => {
            const isExpanded = Boolean(expandedProvisions[provision.id]);
            const remitos =
              provision.ingresos?.filter((ingreso) => ingreso.tipo === "Remito Proveedor") || [];

            return (
              <Card
                key={provision.id}
                sx={{
                  border: isExpanded ? "1px solid #4a98ff" : "1px solid #e7ebf1",
                  borderRadius: "11px",
                  boxShadow: "0 4px 14px rgba(37, 52, 81, 0.07)",
                  overflow: "hidden",
                  transition: "border-color 200ms ease, box-shadow 200ms ease",
                }}
              >
                <SoftBox
                  component="button"
                  type="button"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1.5}
                  p={{ xs: 1.5, sm: 2 }}
                  aria-expanded={isExpanded}
                  aria-controls={`provision-${provision.id}-remitos`}
                  onClick={() => onToggleProvision(provision.id)}
                  sx={{
                    width: "100%",
                    border: 0,
                    backgroundColor: isExpanded ? "rgba(74, 152, 255, 0.035)" : "#fff",
                    font: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background-color 200ms ease",
                    "&:hover": { backgroundColor: "#f7faff" },
                    "&:focus-visible": {
                      outline: "3px solid rgba(52, 127, 240, 0.28)",
                      outlineOffset: -3,
                    },
                  }}
                >
                  <SoftBox
                    display="flex"
                    alignItems={{ xs: "flex-start", sm: "baseline" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 0.25, sm: 1.25 }}
                    minWidth={0}
                    flex={1}
                  >
                    <SoftTypography
                      component="span"
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#2682f5", whiteSpace: "nowrap" }}
                    >
                      OP #{provision.id}
                    </SoftTypography>
                    <SoftTypography
                      component="span"
                      variant="body2"
                      color="dark"
                      sx={{ overflowWrap: "anywhere", lineHeight: 1.35 }}
                    >
                      {provision.pedidoCompra?.nombre || "Sin nombre"}
                    </SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" alignItems="center" gap={1} flex="0 0 auto">
                    <SoftBadge badgeContent={remitos.length} color="info" size="sm" circular />
                    <Icon aria-hidden="true" sx={{ color: "#344563" }}>
                      {isExpanded ? "expand_less" : "expand_more"}
                    </Icon>
                  </SoftBox>
                </SoftBox>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <SoftBox
                    id={`provision-${provision.id}-remitos`}
                    sx={{ p: { xs: 1.25, sm: 2 }, pt: 0 }}
                  >
                    {remitos.length === 0 ? (
                      <SoftBox py={2.5} textAlign="center">
                        <SoftTypography variant="body2" color="text">
                          No hay remitos registrados para esta orden
                        </SoftTypography>
                      </SoftBox>
                    ) : (
                      <>
                        <SoftBox
                          display={{ xs: "none", lg: "block" }}
                          sx={{ borderRadius: "10px", overflow: "hidden" }}
                        >
                          <DataTableCustom
                            columns={remitosColumns}
                            data={remitos}
                            loading={false}
                            loadingActions={loadingFactura}
                            actions={remitosActions}
                            actionsWidth="12%"
                            showActions
                            emptyMessage="No hay remitos disponibles"
                            maxHeight="400px"
                            showFooter={false}
                            zebraStripes
                            hover
                            border
                          />
                        </SoftBox>

                        <SoftBox
                          display={{ xs: "flex", lg: "none" }}
                          flexDirection="column"
                          gap={1.25}
                        >
                          {remitos.map((remito) => {
                            const estadoFactura = getFacturaEstado(remito);
                            const fechaPago =
                              remito?.pagosProveedor?.fechaPago ||
                              remito?.pagosProveedor?.fechaEstimadaPago;

                            return (
                              <SoftBox
                                key={remito.id}
                                sx={{
                                  border: "1px solid #e1e7ef",
                                  borderRadius: "10px",
                                  backgroundColor: "#fff",
                                  p: 1.5,
                                }}
                              >
                                <SoftBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  gap={1}
                                  mb={1.5}
                                >
                                  <SoftTypography variant="button" color="dark" fontWeight="bold">
                                    Remito #{remito.id || 0}
                                  </SoftTypography>
                                  <SoftBadge
                                    color={estadoFactura.color}
                                    badgeContent={estadoFactura.label}
                                    size="xs"
                                  />
                                </SoftBox>
                                <SoftBox
                                  display="grid"
                                  gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                                  gap={1.25}
                                >
                                  <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="bold"
                                    >
                                      Entrega
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="dark" display="block">
                                      {remito.fechaEntrega
                                        ? dayjs(remito.fechaEntrega).format("DD/MM/YYYY")
                                        : "-"}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="bold"
                                    >
                                      Ítems
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="dark" display="block">
                                      {remito.movimientoDetalles?.length || 0}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="bold"
                                    >
                                      Pago
                                    </SoftTypography>
                                    <SoftTypography variant="caption" color="dark" display="block">
                                      {fechaPago
                                        ? dayjs(fechaPago).format("DD/MM/YYYY")
                                        : "Sin pago"}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftBox>
                                    <SoftTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="bold"
                                    >
                                      Observaciones
                                    </SoftTypography>
                                    <SoftTypography
                                      variant="caption"
                                      color="dark"
                                      display="block"
                                      sx={{ overflowWrap: "anywhere" }}
                                    >
                                      {remito.observaciones || "-"}
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                                <SoftBox
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={0.75}
                                  mt={1.5}
                                  pt={1.5}
                                  sx={{ borderTop: "1px solid #e7ebf1" }}
                                >
                                  {remitosActions.map((action, actionIndex) => {
                                    if (action.hidden === true || action.hidden?.(remito))
                                      return null;
                                    const tooltip =
                                      typeof action.tooltip === "function"
                                        ? action.tooltip(remito)
                                        : action.tooltip;
                                    const color =
                                      typeof action.color === "function"
                                        ? action.color(remito)
                                        : action.color;
                                    const icon =
                                      typeof action.icon === "function"
                                        ? action.icon(remito)
                                        : action.icon;

                                    return (
                                      <Tooltip key={`${remito.id}-${actionIndex}`} title={tooltip}>
                                        <SoftButton
                                          variant="outlined"
                                          color={color || "dark"}
                                          size="small"
                                          iconOnly
                                          circular
                                          aria-label={tooltip}
                                          disabled={loadingFactura}
                                          onClick={() => action.onClick?.(remito)}
                                          sx={{
                                            width: 34,
                                            height: 34,
                                            minWidth: 34,
                                            minHeight: 34,
                                            "& .MuiIcon-root": { fontSize: "1rem !important" },
                                          }}
                                        >
                                          <Icon>{icon}</Icon>
                                        </SoftButton>
                                      </Tooltip>
                                    );
                                  })}
                                </SoftBox>
                              </SoftBox>
                            );
                          })}
                        </SoftBox>
                      </>
                    )}
                  </SoftBox>
                </Collapse>
              </Card>
            );
          })}
        </SoftBox>
      )}

      {!loading && pagination.pages > 0 && (
        <CustomPagination
          loading={false}
          length={provisiones.length}
          total={pagination.total}
          pages={pagination.pages}
          page={pagination.page}
          setPage={onPageChange}
        />
      )}
    </SoftBox>
  );
}

FacturacionTab.propTypes = {
  loading: PropTypes.bool,
  provisiones: PropTypes.arrayOf(PropTypes.object),
  search: PropTypes.string.isRequired,
  estado: PropTypes.string.isRequired,
  fecha: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onEstadoChange: PropTypes.func.isRequired,
  onFechaChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  expandedProvisions: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggleProvision: PropTypes.func.isRequired,
  remitosColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  remitosActions: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingFactura: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pages: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
};

FacturacionTab.defaultProps = {
  loading: false,
  provisiones: [],
  loadingFactura: false,
};

export default FacturacionTab;
